/* eslint-disable no-lone-blocks */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Form, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import { FormInstance } from "antd/lib";
import { borderGatesData } from "../../../../constants";
import dayjs from "dayjs";
import { useLocation } from "react-router";
import { useAdministrativeUnitStore } from "../../../../stores";

interface TripInfoProps {
  form: FormInstance<IVisaRequestFormData>;
  setSelectedEntryPurpose: React.Dispatch<React.SetStateAction<string>>;
  initProvince: string | undefined;
  initDistrict: string | undefined;
  initWard: string | undefined;
  init: boolean;
  setInit: React.Dispatch<React.SetStateAction<boolean>>;
}

const TripInfo: React.FC<TripInfoProps> = ({
  form,
  setSelectedEntryPurpose,
  initProvince,
  initDistrict,
  initWard,
  init,
  setInit,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { listProvinces, setListProvinces, setListDistricts, setListWards } =
    useAdministrativeUnitStore();

  const [provinces, setProvinces] = useState<IProvince[]>(listProvinces); // Provinces list
  const [selectedProvince, setSelectedProvince] = useState<string>("");

  const formatProvinceName = (str: string, type: string = "none") => {
    return type === "province"
      ? str
          .replace(/_/g, " ")
          .replace(/^(Thanh Pho|Tinh)\s+/i, "")
          .toUpperCase()
      : str.replace(/_/g, " ").toUpperCase();
  };

  // Handle province logic
  useEffect(() => {
    if (selectedProvince && init) {
      const fetchDistricts = () => {
        const pData = provinces.find(
          (province: IProvince) => province.codename === selectedProvince
        );
        const provinceData = provinces.find(
          (province: IProvince) => province.code === pData?.code
        );
        const districtData = provinceData?.districts;

        if (districtData) {
          form.setFieldValue("district", undefined); // Reset district field
          form.setFieldValue("ward", undefined); // Reset ward field

          setListDistricts(districtData || []);

          setListWards([]);
        }
      };
      fetchDistricts();
    }
  }, [selectedProvince]);

  useEffect(() => {
    const fetchProvincesData = async () => {
      const response = await fetch(
        "https://provinces.open-api.vn/api/?depth=3"
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error("Failed to fetch provinces data");
      }

      setProvinces(data);
      setListProvinces(data);
    };

    if (listProvinces.length === 0) fetchProvincesData();
  }, []);

  useEffect(() => {
    if (initProvince && !init) {
      const provinceData = provinces.find((province: IProvince) =>
        formatProvinceName(province.codename).includes(initProvince)
      );
      form.setFieldValue("province", provinceData?.codename);
      setListDistricts(provinceData?.districts || []);

      if (initDistrict) {
        const districtData = provinceData?.districts.find(
          (district: IDistrict) =>
            formatProvinceName(district.short_codename).includes(initDistrict)
        );
        form.setFieldValue("district", districtData?.codename);
        setListWards(districtData?.wards || []);

        if (initWard) {
          const wardData = districtData?.wards.find((ward: IWard) =>
            formatProvinceName(ward.short_codename).includes(initWard)
          );
          form.setFieldValue("ward", wardData?.codename);
        }
      }

      setSelectedProvince(provinceData?.codename || "");
    }
  }, [initProvince, initDistrict, initWard]);

  return (
    <>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <Form.Item
          layout={"vertical"}
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.grant_valid_from")} (DD/MM/YYYY)
            </div>
          }
          name="intended_date_of_entry"
          className="xl:col-span-2"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_grant_valid_from", {
                ns: "message",
              }),
            },
            () => ({
              validator(_, value) {
                if (!value || dayjs(value).isAfter(dayjs(), "day")) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(
                    t("registration_form.invalid_grant_valid_from", {
                      ns: "message",
                    })
                  )
                );
              },
            }),
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
            className="w-full"
          />
        </Form.Item>

        <Form.Item
          layout={"vertical"}
          label={
            <div className="text-primary-blue">
              {t("profile_search_result.to_date")} (DD/MM/YYYY)
            </div>
          }
          name="to_date"
          className="xl:col-span-2"
          rules={[
            () => ({
              validator(_, value) {
                if (
                  (value &&
                    dayjs(value).isAfter(
                      form.getFieldValue("intended_date_of_entry"),
                      "day"
                    )) ||
                  !value
                ) {
                  if (location.state?.typeVisa.includes("OneMonth")) {
                    if (
                      dayjs(value).diff(
                        form.getFieldValue("intended_date_of_entry"),
                        "day"
                      ) <= 35
                    )
                      return Promise.resolve();
                  } else if (location.state?.typeVisa.includes("ThreeMonth")) {
                    if (
                      dayjs(value).diff(
                        form.getFieldValue("intended_date_of_entry"),
                        "day"
                      ) <= 95
                    )
                      return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(
                      t("registration_form.invalid_to_date_2", {
                        ns: "message",
                      })
                    )
                  );
                } else {
                  return Promise.reject(
                    new Error(
                      t("registration_form.invalid_to_date", {
                        ns: "message",
                      })
                    )
                  );
                }
              },
            }),
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
            className="w-full"
          />
        </Form.Item>

        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.entry_purpose")}
            </div>
          }
          name="entry_purpose"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_purpose_of_entry", {
                ns: "message",
              }),
            },
          ]}
        >
          <Select
            placeholder={t("registration_form.step6.entry_purpose_hint")}
            onChange={(value) => {
              setSelectedEntryPurpose(value);
              form.setFieldsValue({
                company_name: "",
                company_address: "",
                company_phone_number: "",
              });
            }}
          >
            <Select.Option value="Tourist">
              {t("entry_purpose_options.tourist")}
            </Select.Option>
            <Select.Option value="Visiting relatives">
              {t("entry_purpose_options.visiting_relatives")}
            </Select.Option>
            <Select.Option value="Working">
              {t("entry_purpose_options.working")}
            </Select.Option>
            <Select.Option value="Business">
              {t("entry_purpose_options.business")}
            </Select.Option>
            <Select.Option value="Other">
              {t("entry_purpose_options.other")}
            </Select.Option>
          </Select>
        </Form.Item>

        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.province")}
            </div>
          }
          name="province"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_province", { ns: "message" }),
            },
          ]}
        >
          <Select
            showSearch
            placeholder={t("registration_form.step6.province_hint")}
            onChange={(value) => {
              if (!init) setInit(true);
              // form.setFieldValue("province", formatProvinceName(value));
              setSelectedProvince(value);
            }}
            options={provinces.map((province) => ({
              value: province.codename,
              label: formatProvinceName(province.codename, "province"),
            }))}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item> */}

        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.district")}
            </div>
          }
          name="district"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_district", { ns: "message" }),
            },
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            placeholder={t("registration_form.step6.district_hint")}
            onChange={(value) => {
              // form.setFieldValue("district", value);
              setSelectedDistrict(value);
            }}
            options={districts.map((district) => ({
              value: district.codename,
              label: formatProvinceName(district.short_codename),
            }))}
          />
        </Form.Item> */}

        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.ward")}
            </div>
          }
          name="ward"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_ward", { ns: "message" })
            }
          ]}
        >
          <Select
            showSearch
            placeholder={t("registration_form.step6.ward_hint")}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={wards.map((ward) => ({
              value: ward.codename,
              label: formatProvinceName(ward.short_codename)
            }))}
            // onChange={(value) => {
            //   form.setFieldValue("ward", value);
            // }}
          />
        </Form.Item> */}

        {/* <Form.Item
          layout={"vertical"}
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.intended_date_of_entry")}
            </div>
          }
          name="intended_date_of_entry"
          style={{ color: "red" }}
          rules={[
            {
              required: true,
              message: t("registration_form.empty_intended_date_of_entry", {
                ns: "message"
              })
            },
            () => ({
              validator(_, value) {
                if (!value || dayjs(value).isAfter(dayjs(), "day")) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(
                    t("registration_form.invalid_intended_date_of_entry", {
                      ns: "message"
                    })
                  )
                );
              }
            })
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            className="w-full"
            placeholder={t(
              "registration_form.step6.intended_date_of_entry_hint"
            )}
          />
        </Form.Item>
        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.intended_length_of_stay")}
            </div>
          }
          name="intended_length_of_stay"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_intended_length_of_stay", {
                ns: "message"
              })
            },
            () => ({
              validator(_, value) {
                if (!value || value <= 90) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    t("registration_form.invalid_intended_length_of_stay", {
                      ns: "message"
                    })
                  )
                );
              }
            })
          ]}
        >
          <Input
            placeholder={t(
              "registration_form.step6.intended_length_of_stay_hint"
            )}
            type="number"
          />
        </Form.Item> */}

        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.number_in_vn")}
            </div>
          }
          name="vietnam_phone_number"
          rules={[
            {
              pattern: new RegExp(
                "^(\\+84|84|0)(3[2-9]|5[6|8|9]|7[0|6-9]|8[1-5|8|9]|9[0-9])[0-9]{7}$"
              ),
              message: t("registration_form.invalid_telephone_number", {
                ns: "message"
              })
            }
          ]}
        >
          <Input placeholder={t("registration_form.step6.number_in_vn_hint")} />
        </Form.Item> */}
        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.address_in_vn")}
            </div>
          }
          name="vietnam_residental_address"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_address_in_vn", {
                ns: "message"
              })
            }
          ]}
        >
          <Input
            placeholder={t("registration_form.step6.address_in_vn_hint")}
          />
        </Form.Item> */}

        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.intended_border_gate_of_entry")}
            </div>
          }
          name="intended_entry_border_gate"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_intended_border_entry", {
                ns: "message",
              }),
            },
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={borderGatesData.map((borderGate, index) => ({
              key: index,
              value: borderGate.code,
              label:
                borderGate.name[localStorage.getItem("i18nextLng") as string],
            }))}
            placeholder={t(
              "registration_form.step6.intended_border_gate_of_entry_hint"
            )}
          />
        </Form.Item>

        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.intended_border_gate_of_exit")}
            </div>
          }
          name="intended_exit_border_gate"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_intended_border_exit", {
                ns: "message",
              }),
            },
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={borderGatesData.map((borderGate, index) => ({
              key: index,
              value: borderGate.code,
              label:
                borderGate.name[localStorage.getItem("i18nextLng") as string],
            }))}
            placeholder={t(
              "registration_form.step6.intended_border_gate_of_exit_hint"
            )}
          />
        </Form.Item>

        {/* <Form.Item
          layout={"vertical"}
          className="sm:col-span-2 flex items-end"
          name="committed_declaration"
          valuePropName="checked"
          rules={[
            {
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    t("registration_form.empty_committed_declaration", {
                      ns: "message",
                    })
                  )
                );
              },
            },
          ]}
        >
          <Checkbox className="text-primary-blue">
            {t("registration_form.step6.committed")}
          </Checkbox>
        </Form.Item> */}
      </div>
      {/* <div className="flex flex-col gap-6 mbh-8">
        <div className="w-full font-semibold text-lg text-primary-blue">
          {t("registration_form.step6.note")}
        </div>
        <ul className="list-disc list-inside space-y-2 text-primary-blue">
          <li>{t("registration_form.step6.note1")}</li>
          <li>{t("registration_form.step6.note2")}</li>
          <li>{t("registration_form.step6.note3")}</li>
        </ul>
      </div> */}

      {/* <div className="space-y-4">
        <div className="flex flex-col border-b-[1px] border-primary-border pb-6">
          <div className="flex max-md:flex-col max-md:gap-4 justify-between mb-4">
            <div className="text-primary-blue">
              {t("registration_form.step6.agency")}
            </div>

            <Radio.Group
              className="flex max-md:justify-evenly"
              value={showContactPlans}
              onChange={(e) => {
                setShowContactPlans(e.target.value);
                if (!e.target.value) {
                  setContactPlans([
                    {
                      key: "1",
                      name: "",
                      telephone_number: "",
                      address: "",
                      purpose: ""
                    }
                  ]);
                  form.setFieldsValue({ contact_plans: [] });
                }
              }}
            >
              <Radio value={true}>{t("registration_form.yes_option")}</Radio>
              <Radio value={false}>{t("registration_form.no_option")}</Radio>
            </Radio.Group>
          </div>

          {showContactPlans && (
            <ContactPlansTable
              dataSource={contactPlans}
              handleAdd={handleAddContactPlan}
              handleDelete={handleDeleteContactPlan}
              handleChange={handleChangeContactPlan}
            />
          )}
        </div>

        <div className="flex flex-col border-b-[1px] border-primary-border pb-6">
          <div className="flex max-md:flex-col max-md:gap-4 justify-between mb-4">
            <div className="text-primary-blue">
              {t("registration_form.step6.vn_last_01_year")}
            </div>

            <Radio.Group
              className="flex max-md:justify-evenly"
              value={showTrips}
              onChange={(e) => {
                setShowTrips(e.target.value);
                if (!e.target.value) {
                  setVietnamTrips([
                    {
                      key: "1",
                      from_date: "",
                      to_date: "",
                      purpose_of_trip: ""
                    }
                  ]);
                  form.setFieldsValue({ trips_in_the_last_one_year: [] });
                }
              }}
            >
              <Radio value={true}>{t("registration_form.yes_option")}</Radio>
              <Radio value={false}>{t("registration_form.no_option")}</Radio>
            </Radio.Group>
          </div>

          {showTrips && (
            <VietnamTripTable
              dataSource={vietnamTrips}
              handleAdd={handleAddVietnamTrip}
              handleDelete={handleDeleteVietnamTrip}
              handleChange={handleChangeVietnamTrip}
            />
          )}
        </div>

        <div className="flex flex-col border-b-[1px] border-primary-border pb-6">
          <div className="flex max-md:flex-col max-md:gap-4 justify-between mb-4">
            <div className="text-primary-blue">
              {t("registration_form.step6.relative_in_vn")}
            </div>

            <Radio.Group
              className="flex max-md:justify-evenly"
              value={showRelatives}
              onChange={(e) => {
                setShowRelatives(e.target.value);
                if (!e.target.value) {
                  setRelatives([
                    {
                      key: "1",
                      fullname: "",
                      date_of_birth: "",
                      nationality: "",
                      relationship: "",
                      residental_address: ""
                    }
                  ]);
                  form.setFieldsValue({ relatives_in_vietnam: [] });
                }
              }}
            >
              <Radio value={true}>{t("registration_form.yes_option")}</Radio>
              <Radio value={false}>{t("registration_form.no_option")}</Radio>
            </Radio.Group>
          </div>

          {showRelatives && (
            <RelativesTable
              dataSource={relatives}
              handleAdd={handleAddRelative}
              handleDelete={handleDeleteRelative}
              handleChange={handleChangeRelative}
            />
          )}
        </div>
      </div> */}
    </>
  );
};

export default TripInfo;

// // Contact plans data
// const [showContactPlans, setShowContactPlans] = useState<boolean>(
//   form.getFieldValue("contact_plans")?.length > 0
// );
// const [contactPlans, setContactPlans] = useState<IContactPlanInfo[]>(
//   form.getFieldValue("contact_plans")?.length > 0
//     ? form.getFieldValue("contact_plans")
//     : [
//         {
//           key: "1",
//           name: "",
//           telephone_number: "",
//           address: "",
//           purpose: ""
//         }
//       ]
// );
// const handleAddContactPlan = () => {
//   const newData: IContactPlanInfo = {
//     key: Date.now().toString(),
//     name: "",
//     telephone_number: "",
//     address: "",
//     purpose: ""
//   };

//   setContactPlans([...contactPlans, newData]);
//   form.setFieldsValue({ contact_plans: [...contactPlans, newData] });
// };
// const handleDeleteContactPlan = (key: string) => {
//   const data = contactPlans.filter((item) => item.key !== key);

//   setContactPlans(data);
//   form.setFieldsValue({ contact_plans: data });
// };
// const handleChangeContactPlan = (
//   key: string,
//   field: keyof IContactPlanInfo,
//   value: string
// ) => {
//   const data = contactPlans.map((item) =>
//     item.key === key ? { ...item, [field]: value } : item
//   );

//   setContactPlans(data);
//   form.setFieldsValue({ contact_plans: data });
// };

// // Vietnam Trip data
// const [showTrips, setShowTrips] = useState<boolean>(
//   form.getFieldValue("trips_in_the_last_one_year")?.length > 0
// );
// const [vietnamTrips, setVietnamTrips] = useState<ITripInfo[]>(
//   form.getFieldValue("trips_in_the_last_one_year")?.length > 0
//     ? form.getFieldValue("trips_in_the_last_one_year")
//     : [
//         {
//           key: "1",
//           from_date: "",
//           to_date: "",
//           purpose_of_trip: ""
//         }
//       ]
// );
// const handleAddVietnamTrip = () => {
//   const newData: ITripInfo = {
//     key: Date.now().toString(),
//     from_date: "",
//     to_date: "",
//     purpose_of_trip: ""
//   };

//   setVietnamTrips([...vietnamTrips, newData]);
//   form.setFieldsValue({
//     trips_in_the_last_one_year: [...vietnamTrips, newData]
//   });
// };
// const handleDeleteVietnamTrip = (key: string) => {
//   const data = vietnamTrips.filter((item) => item.key !== key);

//   setVietnamTrips(data);
//   form.setFieldsValue({ trips_in_the_last_one_year: data });
// };
// const handleChangeVietnamTrip = (
//   key: string,
//   field: keyof ITripInfo,
//   value: string
// ) => {
//   const data = vietnamTrips.map((item) =>
//     item.key === key ? { ...item, [field]: value } : item
//   );

//   setVietnamTrips(data);
//   form.setFieldsValue({ trips_in_the_last_one_year: data });
// };

// // Relatives data
// const [showRelatives, setShowRelatives] = useState<boolean>(
//   form.getFieldValue("relatives_in_vietnam")?.length > 0
// );
// const [relatives, setRelatives] = useState<IRelativeInfo[]>(
//   form.getFieldValue("relatives_in_vietnam")?.length > 0
//     ? form.getFieldValue("relatives_in_vietnam")
//     : [
//         {
//           key: "1",
//           fullname: "",
//           date_of_birth: "",
//           nationality: "",
//           relationship: "",
//           residental_address: ""
//         }
//       ]
// );
// const handleAddRelative = () => {
//   const newData: IRelativeInfo = {
//     key: Date.now().toString(),
//     fullname: "",
//     date_of_birth: "",
//     nationality: "",
//     relationship: "",
//     residental_address: ""
//   };

//   setRelatives([...relatives, newData]);
//   form.setFieldsValue({ relatives_in_vietnam: [...relatives, newData] });
// };
// const handleDeleteRelative = (key: string) => {
//   const data = relatives.filter((item) => item.key !== key);

//   setRelatives(data);
//   form.setFieldsValue({ relatives_in_vietnam: data });
// };
// const handleChangeRelative = (
//   key: string,
//   field: keyof IRelativeInfo,
//   value: string
// ) => {
//   const data = relatives.map((item) =>
//     item.key === key ? { ...item, [field]: value } : item
//   );

//   setRelatives(data);
//   form.setFieldsValue({ relatives_in_vietnam: data });
// };

// import { level1s, findLevel1ById } from "dvhcvn";
// import type { Level1, Level2, Level3 } from "dvhcvn";

// const [districts, setDistricts] = useState<Level2[]>([]); // Districts list
// const [wards, setWards] = useState<Level3[]>([]); // Wards list

// Fetch districts when province is selected
// useEffect(() => {
//   if (selectedProvince) {
//     const fetchDistricts = () => {
//       const provinceData = findLevel1ById(selectedProvince);
//       const districtData = provinceData?.children;

//       if (districtData) {
//         setDistricts(districtData || []); // Update districts list
//         setWards([]); // Reset wards list
//       }
//     };
//     fetchDistricts();
//   }
// }, [selectedProvince]);

// useEffect(() => {
//   if (selectedDistrict) {
//     const fetchWards = () => {
//       const provinceData = findLevel1ById(selectedProvince);
//       const districtData = provinceData?.findLevel2ById(selectedDistrict);
//       const wardData = districtData?.children;

//       if (wardData) {
//         setWards(wardData || []); // Update wards list
//       }
//     };
//     fetchWards();
//   }
// }, [selectedDistrict]);

{
  /* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.province")}
            </div>
          }
          name="province"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_province", { ns: "message" })
            }
          ]}
        >
          <Select
            showSearch
            placeholder={t("registration_form.step6.province_hint")}
            onChange={(value) => setSelectedProvince(value)}
          >
            {level1s.map((province: Level1, index) => (
              <Select.Option key={index} value={province.id}>
                {province.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.district")}
            </div>
          }
          name="district"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_district", { ns: "message" })
            }
          ]}
        >
          <Select
            showSearch
            placeholder={t("registration_form.step6.district_hint")}
            onChange={(value) => setSelectedDistrict(value)}
          >
            {districts &&
              districts.map((district: Level2, index) => (
                <Select.Option key={index} value={district.id}>
                  {district.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <div className="text-primary-blue">
              {t("registration_form.step6.ward")}
            </div>
          }
          name="ward"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_ward", { ns: "message" })
            }
          ]}
        >
          <Select
            showSearch
            placeholder={t("registration_form.step6.ward_hint")}
          >
            {wards &&
              wards.map((ward: Level3, index) => (
                <Select.Option key={index} value={ward.id}>
                  {ward.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item> */
}
